import { useEffect } from 'react';
import styled from 'styled-components';
import {
  Button,
  Flex,
  FlexColumn,
  Dialog,
} from '@elementary/common-ui/components';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PULL_REQUESTS,
  TEST_MANAGEMENT_TESTS,
} from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import * as sharedApi from 'shared/services/api';
import useSnackbar from 'shared/hooks/useSnackbar';
import useTicketField from 'shared/hooks/useTicketField';
import { DeleteSingularTestApi, DeleteTestApi, Test } from 'shared/types';
import ConnectTicketDropdown from 'shared/components/ConnectTicketDropdown';
import { CreateTicketDialogs } from 'shared/components/CreateTicketDialogs';
import { getPermissionErrorMessage } from 'shared/utils/permissionErrors';
import { isOnlyCustomQueryTests } from 'testManagement/utils/utils';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import { fontSize } from '@elementary/common-ui/style/utils';
import * as api from '../services/api';
import DeleteTestAccordion from './DeleteTestAccordion';

const Bold = styled(Typography).attrs({ component: 'span' })`
  font-size: inherit;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-inline: 4px;
`;

const SecondaryMessage = styled(Typography)`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.tertiary};
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onDeleteSuccess: () => void;
  selectedTests: Test[];
};

const DeleteTestDialog = ({
  open,
  onClose,
  onDeleteSuccess,
  selectedTests,
}: Props) => {
  const queryClient = useQueryClient();
  const selectedEnvId = useSelectedEnv();
  const navigate = useNavigateWithEnv();
  const { openSnackbar } = useSnackbar();

  const {
    ticketId,
    setTicketId,
    ticketIdRef,
    requireTicketId,
    enabledIntegrations,
    activeIntegration,
    handleIntegrationSelect,
    createTicketDialogOpen,
    handleCreateTicketClick,
    handleCreateTicketClose,
    handleTicketCreated,
    resetTicket,
  } = useTicketField({ envId: selectedEnvId });

  useEffect(() => {
    if (!open) resetTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedCount = selectedTests?.length || 0;
  const hasCloudTests = selectedTests.some((test) => test.is_cloud);
  const hasNonCloudTests = selectedTests.some((test) => !test.is_cloud);

  const onDeleteError = (error: unknown) => {
    if (getPermissionErrorMessage(error) !== null) {
      return;
    }
    openSnackbar({
      message:
        (error as any)?.response?.data?.error_message ||
        `Failed to delete ${pluralizeString(selectedCount, 'test', 'tests')}.`,
      variant: 'error',
    });
  };

  const { mutateAsync: deleteTests, isLoading: isDeleteTestLoading } =
    useMutation(
      (value: DeleteTestApi) =>
        api.deleteTests(
          value,
          selectedEnvId!,
          ticketIdRef.current || undefined,
        ),
      {
        onSuccess: () => {
          onClose();
        },
        onError: onDeleteError,
      },
    );

  const {
    mutateAsync: deleteSingularTests,
    isLoading: isDeleteSingularTestLoading,
  } = useMutation(
    (value: DeleteSingularTestApi) =>
      api.deleteSingularTests(
        value,
        selectedEnvId!,
        ticketIdRef.current || undefined,
      ),
    {
      onSuccess: () => {
        onClose();
      },
      onError: onDeleteError,
    },
  );

  const {
    mutateAsync: deleteCloudTests,
    isLoading: isDeleteCloudTestsLoading,
  } = useMutation(
    async (ids: string[]) => {
      const tests = ids.map((id) => ({
        test_id: id,
        disabled: true,
      }));
      return await sharedApi.updateCloudTests(selectedEnvId!, tests);
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([TEST_MANAGEMENT_TESTS, selectedEnvId], {
          exact: false,
        });
        openSnackbar({
          message:
            (res.length > 1
              ? `${res.length} cloud tests were`
              : 'Cloud test was') + ' deleted successfully.',
          variant: 'success',
        });
      },
      onError: onDeleteError,
    },
  );

  const handleDeleteConfirm = async () => {
    let isDbtTest = true;
    const isOnlyCustomQueryTest = isOnlyCustomQueryTests(selectedTests);

    if (isOnlyCustomQueryTest) {
      const deleteTestsArg: DeleteSingularTestApi = {
        test_ids: selectedTests.map((item) => item.id),
      };

      await deleteSingularTests(deleteTestsArg);
    } else if (hasCloudTests) {
      deleteCloudTests(
        selectedTests.filter((item) => item.is_cloud).map((item) => item.id),
      );
      isDbtTest = false;
      // closing dialog before deletion completed
      onClose();
    } else {
      const deleteTestsArg: DeleteTestApi = selectedTests.map((item) => ({
        test_id: item.id,
        resource_name: item.model_name || item.table,
        column: item.column,
        package: item.package,
        name: item.name,
        args: item.args,
        source_name: item.source_name,
        owners: item.owners,
        tags: item.tags,
        meta: item.meta,
      }));

      await deleteTests(deleteTestsArg);
    }

    onDeleteSuccess();

    if (isDbtTest) {
      queryClient.invalidateQueries([PULL_REQUESTS]);
      navigate('/pull-requests');
    }
  };

  return (
    <>
      <Dialog
        customWidth={1024}
        open={open}
        isLoading={
          isDeleteCloudTestsLoading ||
          isDeleteTestLoading ||
          isDeleteSingularTestLoading
        }
        onClose={onClose}
        title={`Delete ${pluralizeString(selectedCount, 'test', 'tests')}`}
        onConfirmButtonClick={handleDeleteConfirm}
        titleIcon={<TrashIcon />}
        cancelText="Cancel"
        confirmText={hasNonCloudTests ? 'Submit & Open PR' : 'Delete'}
        message={
          <Typography sx={{ fontSize: fontSize('sm') }}>
            Are you sure you want to delete{' '}
            <Bold>{pluralizeString(selectedCount, 'test', 'tests')}</Bold>?
          </Typography>
        }
        confirmDisabled={requireTicketId && hasNonCloudTests && !ticketId}
        secondaryMessage={
          <FlexColumn>
            {hasNonCloudTests ? (
              <SecondaryMessage>
                {`These ${pluralizeString(
                  selectedCount,
                  'test is',
                  'tests are',
                )} managed in code and cannot be deleted directly from the UI. Submitting will open a PR; deletion will take effect once it's approved and merged.`}
              </SecondaryMessage>
            ) : (
              <SecondaryMessage>
                {`${
                  selectedCount > 1 ? 'These are' : 'This is'
                } cloud-managed ${
                  selectedCount > 1 ? 'tests' : 'test'
                } and will be permanently deleted immediately. This action cannot be undone.`}
              </SecondaryMessage>
            )}

            <DeleteTestAccordion selectedItems={selectedTests} />
          </FlexColumn>
        }
        hideCancelButton
        footerRight={
          <Flex
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              size="medium"
              onClick={onClose}
            >
              Cancel
            </Button>
            {activeIntegration && hasNonCloudTests && (
              <>
                <ConnectTicketDropdown
                  envId={selectedEnvId!}
                  enabledIntegrations={enabledIntegrations}
                  onSelect={(integration, ticket) => {
                    handleIntegrationSelect(integration.id);
                    setTicketId(ticket.key);
                  }}
                  onCreateClick={(integration) =>
                    handleCreateTicketClick(integration.id)
                  }
                  selectedTicket={
                    ticketId
                      ? {
                          key: ticketId,
                          integrationType: activeIntegration.type,
                        }
                      : null
                  }
                  onClearSelected={() => setTicketId(null)}
                  popoverPlacement="above"
                />
                <CreateTicketDialogs
                  open={createTicketDialogOpen}
                  onClose={handleCreateTicketClose}
                  onTicketCreated={handleTicketCreated}
                  activeIntegration={activeIntegration}
                  envId={selectedEnvId!}
                />
              </>
            )}
          </Flex>
        }
      />
    </>
  );
};

export default DeleteTestDialog;
